import { useCallback } from "react";
import {
	ComponentTypes,
	type All8PageComponent,
	type All8PageContent,
	type HomeNewsContent,
	type LatestArticleGridContent,
	type LinkTarget,
	type MarketsBoardContent,
	type PaginatedActivityGridContent
} from "@apa/core-renderers";
import type { ContentDocument, ContentTypeConfig } from "../../API/Models";
import { normalizeComponents } from "./pageEditorUtils";
import { projectMarketsInflation } from "./projectMarketsInflation";

const DEFAULT_LANGUAGE = "fr";

export type DynamicContentResolversDeps = {
	taxonomyMap: Map<string, string>;
	categoryKeyMap: Map<string, string>;
	categoryTitleMap: Map<string, string>;
	contentTypeConfigs: ContentTypeConfig[];
	draftLanguage: string | null | undefined;
	newsSectionItemsByType: Record<string, ContentDocument[]>;
	newsSectionDynamicPages: Record<string, ContentDocument>;
	paginatedActivityItemsByType: Record<string, ContentDocument[]>;
	latestArticleItemsByType: Record<string, ContentDocument[]>;
	/** Items keyed by the content type used by the inflation block on a
	 * MarketsBoard component. Mirrors `newsSectionItemsByType` etc. */
	marketsBoardInflationItemsByType: Record<string, ContentDocument[]>;
	formatVariableValue: (rawValue: any, fieldType?: string | null) => string;
	stringifyVariableValue: (value: any) => string;
	parseDynamicDate: (rawValue: any) => Date | null;
	formatDynamicDate: (rawValue: any) => string;
	applyArticleHeroBreadcrumbs: (component: All8PageComponent, data: Record<string, any>) => All8PageComponent;
};

/**
 * Hook that provides dynamic content resolution functions.
 * These resolve NewsSection, PaginatedActivityGrid, and LatestArticleGrid
 * components that use "dynamic" mode, as well as variable-based template resolution.
 */
export function useDynamicContentResolvers(deps: DynamicContentResolversDeps) {
	const {
		taxonomyMap,
		categoryKeyMap,
		categoryTitleMap,
		contentTypeConfigs,
		draftLanguage,
		newsSectionItemsByType,
		newsSectionDynamicPages,
		paginatedActivityItemsByType,
		latestArticleItemsByType,
		marketsBoardInflationItemsByType,
		formatVariableValue,
		stringifyVariableValue,
		parseDynamicDate,
		formatDynamicDate,
		applyArticleHeroBreadcrumbs
	} = deps;

	const resolveNewsHref = useCallback((value: any): LinkTarget => {
		if (value && typeof value === "object") {
			const slug = (value as { slug?: string }).slug;
			const pageId = (value as { pageId?: string }).pageId;
			const external = (value as { external?: string }).external;
			if (external) return { external };
			if (pageId) return { pageId };
			if (slug) return { slug };
		}
		const raw = (value ?? "").toString().trim();
		if (!raw) return { slug: "" };
		if (/^https?:\/\//i.test(raw)) return { external: raw };
		return { slug: raw };
	}, []);

	const normalizeSlugValue = useCallback((value: any): string => {
		return (value ?? "").toString().trim().replace(/^\/+|\/+$/g, "");
	}, []);

	const replaceVariablesInSlug = useCallback((template: string, variables: Record<string, any>): string => {
		if (!template) return "";
		return template.replace(/\{([^{}]+)\}/g, (match, key) => {
			const normalized = (key ?? "").toString().trim();
			if (!normalized) return match;
			const value = variables[normalized];
			return value == null ? "" : String(value);
		});
	}, []);

	const buildDynamicPageSlug = useCallback(
		(template: string, variables: Record<string, any>): string => {
			const rawTemplate = (template ?? "").toString().trim();
			if (!rawTemplate) return "";
			const hasTokens = /\{[^{}]+\}/.test(rawTemplate);
			const resolved = normalizeSlugValue(replaceVariablesInSlug(rawTemplate, variables));
			if (hasTokens) return resolved;
			const pathRaw = variables.path ?? variables.slug ?? variables.id ?? "";
			const path = normalizeSlugValue(pathRaw);
			if (!path) return resolved;
			if (!resolved) return path;
			return normalizeSlugValue(`${resolved}/${path}`);
		},
		[normalizeSlugValue, replaceVariablesInSlug]
	);

	const normalizeContentTypeKey = useCallback((value: any): string => {
		return (value ?? "").toString().trim().toLowerCase();
	}, []);

	const resolveDynamicPageTemplate = useCallback(
		(dynamicPageId: string, contentTypeKey: string): ContentDocument | null => {
			const doc = dynamicPageId ? newsSectionDynamicPages[dynamicPageId] : null;
			if (!doc || doc.isDeleted) return null;
			const data = (doc.data ?? {}) as { dynamicContentTypeId?: string };
			const docType = normalizeContentTypeKey(data.dynamicContentTypeId);
			if (!docType) return null;
			const expected = normalizeContentTypeKey(contentTypeKey);
			if (!expected || docType !== expected) return null;
			return doc;
		},
		[newsSectionDynamicPages, normalizeContentTypeKey]
	);

	const resolveContentTypeConfig = useCallback(
		(contentTypeKey?: string | null): ContentTypeConfig | null => {
			const normalized = (contentTypeKey ?? "").toString().trim().toLowerCase();
			if (!normalized) return null;
			return contentTypeConfigs.find((config) => (config.id ?? "").toString().trim().toLowerCase() === normalized) ?? null;
		},
		[contentTypeConfigs]
	);

	const buildVariableMap = useCallback(
		(item: ContentDocument | null, config?: ContentTypeConfig | null): Record<string, any> => {
			const variables: Record<string, any> = {};
			if (!item) return variables;
			const data = (item.data ?? {}) as Record<string, any>;
			variables.id = item.id ?? "";
			variables.slug = typeof data.slug === "string" ? data.slug : "";
			variables.path = typeof data.path === "string" ? data.path : "";
			if (Array.isArray(data.taxonomyIds)) {
				variables.taxonomies = data.taxonomyIds
					.map((id: string) => taxonomyMap.get(id) ?? id)
					.filter(Boolean)
					.join(", ");
			} else {
				variables.taxonomies = "";
			}
			if (Array.isArray(data.categoryPathIds)) {
				const categoryPath = data.categoryPathIds
					.map((id: string) => categoryKeyMap.get(id) ?? id)
					.filter(Boolean)
					.join("/");
				variables.categoryPath = categoryPath;
				variables.categories = categoryPath;
				const lastCategoryId = data.categoryPathIds[data.categoryPathIds.length - 1];
				variables.categoryTitle = lastCategoryId
					? categoryTitleMap.get(String(lastCategoryId)) ?? String(lastCategoryId)
					: "";
			} else {
				variables.categoryPath = "";
				variables.categories = "";
				variables.categoryTitle = "";
			}

			const configFields = config?.fields ?? [];
			if (configFields.length > 0) {
				const dataByKey = new Map<string, { key: string; value: any }>();
				Object.entries(data).forEach(([key, value]) => {
					dataByKey.set(key.toLowerCase(), { key, value });
				});
				configFields.forEach((field) => {
					const key = (field.key ?? field.title ?? "").trim();
					if (!key) return;
					const entry = dataByKey.get(key.toLowerCase());
					if (!entry) {
						variables[key] = "";
						return;
					}
					const fieldType = (field.type ?? "").trim().toLowerCase();
					if (fieldType === "image") {
						variables[key] = entry.value;
						return;
					}
					variables[key] = formatVariableValue(entry.value, field.type);
				});
			}

			Object.entries(data).forEach(([key, value]) => {
				if (variables[key] !== undefined) return;
				variables[key] = formatVariableValue(value, null);
			});
			return variables;
		},
		[taxonomyMap, categoryKeyMap, categoryTitleMap, formatVariableValue]
	);

	const resolveNewsSectionDynamicContent = useCallback(
		(component: All8PageComponent): HomeNewsContent => {
			const content = (component.content ?? {}) as HomeNewsContent;
			if (component.componentType !== ComponentTypes.NewsSection) return content;
			if (content.mode !== "dynamic") return content;
			const dynamic = content.dynamicComponent ?? {};
			const contentTypeKey = (dynamic.contentTypeKey ?? "").toString().trim();
			if (!contentTypeKey) return content;
			const config = resolveContentTypeConfig(contentTypeKey);
			const items = newsSectionItemsByType[contentTypeKey] ?? [];
			if (items.length === 0) return content;
			const language = (draftLanguage ?? DEFAULT_LANGUAGE).toLowerCase();
			const filteredByLanguage = items.filter(
				(item) => !item?.isDeleted && (!item?.language || item.language === language)
			);
			if (filteredByLanguage.length === 0) return content;
			const sortField = (dynamic.sortField ?? "").toString().trim();
			const featuredRules = Array.isArray(dynamic.featuredFilter) ? dynamic.featuredFilter : [];
			const itemsRules = Array.isArray(dynamic.itemsFilter) ? dynamic.itemsFilter : [];
			const featuredCount = Number.isFinite(Number(dynamic.featuredCount)) ? Math.max(1, Number(dynamic.featuredCount)) : 1;
			const itemsCount = Number.isFinite(Number(dynamic.itemsCount)) ? Math.max(0, Number(dynamic.itemsCount)) : 3;
			const mapping = dynamic.fieldMapping ?? {};
			const dynamicPageId = (dynamic.dynamicPageId ?? "").toString().trim();
			const dynamicPageTemplate = dynamicPageId ? resolveDynamicPageTemplate(dynamicPageId, contentTypeKey) : null;
			const dynamicPageSlugTemplate =
				dynamicPageTemplate && dynamicPageTemplate.data
					? ((dynamicPageTemplate.data as { slug?: string }).slug ?? "")
					: "";

			const getVariables = (item: ContentDocument) => buildVariableMap(item, config);
			const resolveSortDate = (item: ContentDocument, variables: Record<string, any>): Date | null => {
				if (!sortField) {
					return item.updatedAt ? new Date(item.updatedAt as any) : null;
				}
				const raw = variables[sortField] ?? (item.data ?? {})[sortField];
				return parseDynamicDate(raw);
			};
			const matchesRule = (variables: Record<string, any>, rule: any): boolean => {
				const fieldKey = (rule?.field ?? "").toString().trim();
				if (!fieldKey) return true;
				const expected = rule?.value;
				if (expected === "" || expected === null || expected === undefined) return true;
				const actual = variables[fieldKey];
				if (typeof expected === "boolean") {
					return Boolean(actual) === expected;
				}
				if (typeof expected === "number") {
					const numeric = Number(actual);
					return Number.isFinite(numeric) && numeric === expected;
				}
				const actualText = stringifyVariableValue(actual).trim().toLowerCase();
				const expectedText = stringifyVariableValue(expected).trim().toLowerCase();
				return actualText === expectedText;
			};
			const applyRules = (source: ContentDocument[], rules: any[]) =>
				source.filter((item) => {
					const variables = getVariables(item);
					return rules.every((rule) => matchesRule(variables, rule));
				});
			const sortByDate = (source: ContentDocument[]) =>
				[...source].sort((a, b) => {
					const varsA = getVariables(a);
					const varsB = getVariables(b);
					const dateA = resolveSortDate(a, varsA)?.getTime() ?? 0;
					const dateB = resolveSortDate(b, varsB)?.getTime() ?? 0;
					return dateB - dateA;
				});

			const featuredCandidates = sortByDate(applyRules(filteredByLanguage, featuredRules));
			const featuredItem = featuredCandidates.slice(0, featuredCount)[0] ?? null;
			const featuredVariables = featuredItem ? getVariables(featuredItem) : null;

			const featuredId =
				featuredItem?.id ??
				(featuredVariables?.slug ?? featuredVariables?.path ?? featuredVariables?.id ?? "");
			const itemsCandidates = sortByDate(applyRules(filteredByLanguage, itemsRules)).filter((item) => {
				if (!featuredId) return true;
				const variables = getVariables(item);
				const candidateId = item.id ?? variables.slug ?? variables.path ?? variables.id ?? "";
				return candidateId !== featuredId;
			});

			const mapItem = (item: ContentDocument): HomeNewsContent["featured"] => {
				const variables = getVariables(item);
				const titleKey = (mapping.title ?? "").toString().trim();
				const excerptKey = (mapping.excerpt ?? "").toString().trim();
				const imageKey = (mapping.image ?? "").toString().trim();
				const hrefKey = (mapping.href ?? "").toString().trim();
				const title = titleKey ? stringifyVariableValue(variables[titleKey]) : "";
				const excerpt = excerptKey ? stringifyVariableValue(variables[excerptKey]) : "";
				const image = imageKey ? variables[imageKey] : undefined;
				const hrefValue = hrefKey ? variables[hrefKey] : variables.slug ?? variables.path ?? "";
				const dynamicSlug = dynamicPageSlugTemplate ? buildDynamicPageSlug(dynamicPageSlugTemplate, variables) : "";
				return {
					id: item.id ?? (variables.id as string | undefined),
					title,
					excerpt,
					image,
					href: dynamicSlug ? { slug: dynamicSlug } : resolveNewsHref(hrefValue)
				};
			};

			const resolvedFeatured = featuredItem ? mapItem(featuredItem) : content.featured;
			const resolvedItems = itemsCandidates.slice(0, itemsCount).map(mapItem);

			return {
				...content,
				featured: resolvedFeatured,
				items: resolvedItems
			};
		},
		[
			buildDynamicPageSlug,
			buildVariableMap,
			contentTypeConfigs,
			draftLanguage,
			newsSectionItemsByType,
			parseDynamicDate,
			resolveContentTypeConfig,
			resolveDynamicPageTemplate,
			resolveNewsHref,
			stringifyVariableValue
		]
	);

	const resolvePaginatedActivityGridDynamicContent = useCallback(
		(component: All8PageComponent): PaginatedActivityGridContent => {
			const content = (component.content ?? {}) as PaginatedActivityGridContent;
			if (component.componentType !== ComponentTypes.PaginatedActivityGrid) return content;
			if (content.mode !== "dynamic") return content;
			const dynamic = content.dynamicComponent ?? {};
			const contentTypeKey = (dynamic.contentTypeKey ?? "").toString().trim();
			if (!contentTypeKey) return content;
			const config = resolveContentTypeConfig(contentTypeKey);
			if (!config) return content;
			const items = paginatedActivityItemsByType[contentTypeKey] ?? [];
			if (items.length === 0) return content;
			const language = (draftLanguage ?? DEFAULT_LANGUAGE).toLowerCase();
			const filteredByLanguage = items.filter(
				(item) => !item?.isDeleted && (!item?.language || item.language === language)
			);
			if (filteredByLanguage.length === 0) return content;

			const sortField = (dynamic.sortField ?? "").toString().trim();
			const filterRules = Array.isArray(dynamic.filters) ? dynamic.filters : [];
			const itemsPerPage = Number.isFinite(Number(dynamic.itemsPerPage)) ? Math.max(1, Number(dynamic.itemsPerPage)) : 24;
			const resolveBool = (value: any, fallback: boolean) => {
				if (typeof value === "boolean") return value;
				if (typeof value === "number") return value !== 0;
				if (typeof value === "string") {
					const normalized = value.trim().toLowerCase();
					if (normalized === "true") return true;
					if (normalized === "false") return false;
					if (normalized === "1") return true;
					if (normalized === "0") return false;
				}
				return fallback;
			};
			const enableTaxonomyFilter = resolveBool(dynamic.enableTaxonomyFilter, true);
			const enableCategoryFilter = resolveBool(dynamic.enableCategoryFilter, true);
			const searchPlaceholder = (dynamic.searchPlaceholder ?? content.searchPlaceholder ?? "").toString().trim();
			const cardType = (dynamic.cardType ?? content.cardType ?? "").toString().trim();
			const mapping = dynamic.fieldMapping ?? {};
			const dynamicPageId = (dynamic.dynamicPageId ?? "").toString().trim();
			const dynamicPageTemplate = dynamicPageId ? resolveDynamicPageTemplate(dynamicPageId, contentTypeKey) : null;
			const dynamicPageSlugTemplate =
				dynamicPageTemplate && dynamicPageTemplate.data
					? ((dynamicPageTemplate.data as { slug?: string }).slug ?? "")
					: "";

			const getVariables = (item: ContentDocument) => buildVariableMap(item, config);
			const resolveSortDate = (item: ContentDocument, variables: Record<string, any>): Date | null => {
				if (!sortField) {
					return item.updatedAt ? new Date(item.updatedAt as any) : null;
				}
				const raw = variables[sortField] ?? (item.data ?? {})[sortField];
				return parseDynamicDate(raw);
			};
			const matchesRule = (variables: Record<string, any>, rule: any): boolean => {
				const fieldKey = (rule?.field ?? "").toString().trim();
				if (!fieldKey) return true;
				const expected = rule?.value;
				if (expected === "" || expected === null || expected === undefined) return true;
				const actual = variables[fieldKey];
				if (typeof expected === "boolean") {
					return Boolean(actual) === expected;
				}
				if (typeof expected === "number") {
					const numeric = Number(actual);
					return Number.isFinite(numeric) && numeric === expected;
				}
				const actualText = stringifyVariableValue(actual).trim().toLowerCase();
				const expectedText = stringifyVariableValue(expected).trim().toLowerCase();
				return actualText === expectedText;
			};
			const applyRules = (source: ContentDocument[], rules: any[]) =>
				source.filter((item) => {
					const variables = getVariables(item);
					return rules.every((rule) => matchesRule(variables, rule));
				});
			const sortByDate = (source: ContentDocument[]) =>
				[...source].sort((a, b) => {
					const varsA = getVariables(a);
					const varsB = getVariables(b);
					const dateA = resolveSortDate(a, varsA)?.getTime() ?? 0;
					const dateB = resolveSortDate(b, varsB)?.getTime() ?? 0;
					return dateB - dateA;
				});

			const resolvedItems = sortByDate(applyRules(filteredByLanguage, filterRules)).map((item) => {
				const variables = getVariables(item);
				const titleKey = (mapping.title ?? "").toString().trim();
				const descriptionKey = (mapping.description ?? "").toString().trim();
				const imageKey = (mapping.image ?? "").toString().trim();
				const imageAltKey = (mapping.imageAlt ?? "").toString().trim();
				const hrefKey = (mapping.href ?? "").toString().trim();
				const title = titleKey ? stringifyVariableValue(variables[titleKey]) : "";
				const description = descriptionKey ? stringifyVariableValue(variables[descriptionKey]) : "";
				const imageSrc = imageKey ? variables[imageKey] : undefined;
				const imageAltRaw = imageAltKey ? stringifyVariableValue(variables[imageAltKey]) : "";
				const hrefValue = hrefKey ? variables[hrefKey] : variables.slug ?? variables.path ?? "";
				const dynamicSlug = dynamicPageSlugTemplate ? buildDynamicPageSlug(dynamicPageSlugTemplate, variables) : "";
				const data = (item.data ?? {}) as { taxonomyIds?: string[]; categoryPathIds?: string[] };
				return {
					id: item.id ?? (variables.id as string | undefined),
					title,
					description,
					imageSrc,
					imageAlt: imageAltRaw || title,
					href: dynamicSlug ? { slug: dynamicSlug } : resolveNewsHref(hrefValue),
					taxonomyIds: Array.isArray(data.taxonomyIds) ? data.taxonomyIds : [],
					categoryPathIds: Array.isArray(data.categoryPathIds) ? data.categoryPathIds : []
				};
			});
			const pagedItems = itemsPerPage > 0 ? resolvedItems.slice(0, itemsPerPage) : resolvedItems;

			const taxonomyOptions = new Map<string, string>();
			const categoryOptions = new Map<string, { label: string; key: string }>();
			resolvedItems.forEach((item) => {
				(item.taxonomyIds ?? []).forEach((id) => {
					const label = taxonomyMap.get(id) ?? id;
					taxonomyOptions.set(id, label);
				});
				const pathIds = Array.isArray(item.categoryPathIds) ? item.categoryPathIds : [];
				if (pathIds.length > 0) {
					const labelParts: string[] = [];
					const keyParts: string[] = [];
					pathIds.forEach((id) => {
						const label = categoryTitleMap.get(id) ?? id;
						labelParts.push(label);
						const key = categoryKeyMap.get(id) ?? id;
						keyParts.push(key);
						if (!categoryOptions.has(id)) {
							categoryOptions.set(id, {
								label: labelParts.join(" > "),
								key: keyParts.join("/")
							});
						}
					});
				}
			});

			const taxonomies = Array.from(taxonomyOptions, ([id, label]) => ({ id, label })).sort((a, b) =>
				a.label.localeCompare(b.label)
			);
			const categories = Array.from(categoryOptions, ([id, entry]) => ({
				id,
				label: entry.label,
				key: entry.key
			})).sort((a, b) => a.label.localeCompare(b.label));

			const totalItems = resolvedItems.length;
			const totalPages = itemsPerPage > 0 ? Math.ceil(totalItems / itemsPerPage) : 0;

			return {
				...content,
				items: pagedItems,
				pagination: {
					...(content.pagination ?? {}),
					itemsPerPage,
					totalItems,
					totalPages,
					page: 1,
					query: "",
					taxonomyId: "",
					categoryId: "",
					taxonomies,
					categories
				},
				enableTaxonomyFilter,
				enableCategoryFilter,
				searchPlaceholder: searchPlaceholder || content.searchPlaceholder,
				cardType: cardType || content.cardType
			};
		},
		[
			buildDynamicPageSlug,
			buildVariableMap,
			categoryTitleMap,
			contentTypeConfigs,
			draftLanguage,
			paginatedActivityItemsByType,
			parseDynamicDate,
			resolveContentTypeConfig,
			resolveDynamicPageTemplate,
			resolveNewsHref,
			stringifyVariableValue,
			taxonomyMap
		]
	);

	const resolveLatestArticleGridDynamicContent = useCallback(
		(component: All8PageComponent): LatestArticleGridContent => {
			const content = (component.content ?? {}) as LatestArticleGridContent;
			if (component.componentType !== ComponentTypes.LatestArticleGrid) return content;
			if (content.mode !== "dynamic") return content;
			const dynamic = content.dynamicComponent ?? {};
			const contentTypeKey = (dynamic.contentTypeKey ?? "").toString().trim();
			if (!contentTypeKey) return content;
			const config = resolveContentTypeConfig(contentTypeKey);
			if (!config) return content;
			const items = latestArticleItemsByType[contentTypeKey] ?? [];
			if (items.length === 0) return content;
			const language = (draftLanguage ?? DEFAULT_LANGUAGE).toLowerCase();
			const filteredByLanguage = items.filter(
				(item) => !item?.isDeleted && (!item?.language || item.language === language)
			);
			if (filteredByLanguage.length === 0) return content;

			const sortField = (dynamic.sortField ?? "").toString().trim();
			const filterRules = Array.isArray(dynamic.filters) ? dynamic.filters : [];
			const itemsCount = Number.isFinite(Number(dynamic.itemsCount)) ? Math.max(1, Number(dynamic.itemsCount)) : 3;
			const mapping = dynamic.fieldMapping ?? {};
			const dynamicPageId = (dynamic.dynamicPageId ?? "").toString().trim();
			const dynamicPageTemplate = dynamicPageId ? resolveDynamicPageTemplate(dynamicPageId, contentTypeKey) : null;
			const dynamicPageSlugTemplate =
				dynamicPageTemplate && dynamicPageTemplate.data
					? ((dynamicPageTemplate.data as { slug?: string }).slug ?? "")
					: "";

			const getVariables = (item: ContentDocument) => buildVariableMap(item, config);
			const resolveSortDate = (item: ContentDocument, variables: Record<string, any>): Date | null => {
				if (!sortField) {
					return item.updatedAt ? new Date(item.updatedAt as any) : null;
				}
				const raw = variables[sortField] ?? (item.data ?? {})[sortField];
				return parseDynamicDate(raw);
			};
			const matchesRule = (variables: Record<string, any>, rule: any): boolean => {
				const fieldKey = (rule?.field ?? "").toString().trim();
				if (!fieldKey) return true;
				const expected = rule?.value;
				if (expected === "" || expected === null || expected === undefined) return true;
				const actual = variables[fieldKey];
				if (typeof expected === "boolean") {
					return Boolean(actual) === expected;
				}
				if (typeof expected === "number") {
					const numeric = Number(actual);
					return Number.isFinite(numeric) && numeric === expected;
				}
				const actualText = stringifyVariableValue(actual).trim().toLowerCase();
				const expectedText = stringifyVariableValue(expected).trim().toLowerCase();
				return actualText === expectedText;
			};
			const applyRules = (source: ContentDocument[], rules: any[]) =>
				source.filter((item) => {
					const variables = getVariables(item);
					return rules.every((rule) => matchesRule(variables, rule));
				});
			const sortByDate = (source: ContentDocument[]) =>
				[...source].sort((a, b) => {
					const varsA = getVariables(a);
					const varsB = getVariables(b);
					const dateA = resolveSortDate(a, varsA)?.getTime() ?? 0;
					const dateB = resolveSortDate(b, varsB)?.getTime() ?? 0;
					return dateB - dateA;
				});

			const resolvedItems = sortByDate(applyRules(filteredByLanguage, filterRules))
				.slice(0, itemsCount)
				.map((item) => {
					const variables = getVariables(item);
					const categoryKey = (mapping.category ?? "").toString().trim();
					const titleKey = (mapping.title ?? "").toString().trim();
					const excerptKey = (mapping.excerpt ?? "").toString().trim();
					const authorKey = (mapping.author ?? "").toString().trim();
					const authorRoleKey = (mapping.authorRole ?? "").toString().trim();
					const dateKey = (mapping.date ?? "").toString().trim();
					const imageKey = (mapping.image ?? "").toString().trim();
					const hrefKey = (mapping.href ?? "").toString().trim();
					const title = titleKey ? stringifyVariableValue(variables[titleKey]) : "";
					const dateRaw = dateKey ? variables[dateKey] : "";
					const hrefValue = hrefKey ? variables[hrefKey] : variables.slug ?? variables.path ?? "";
					const dynamicSlug = dynamicPageSlugTemplate ? buildDynamicPageSlug(dynamicPageSlugTemplate, variables) : "";
					return {
						id: item.id ?? (variables.id as string | undefined),
						category: categoryKey ? stringifyVariableValue(variables[categoryKey]) : "",
						title,
						excerpt: excerptKey ? stringifyVariableValue(variables[excerptKey]) : "",
						author: authorKey ? stringifyVariableValue(variables[authorKey]) : "",
						authorRole: authorRoleKey ? stringifyVariableValue(variables[authorRoleKey]) : "",
						date: formatDynamicDate(dateRaw),
						imageSrc: imageKey ? variables[imageKey] : undefined,
						href: dynamicSlug ? { slug: dynamicSlug } : resolveNewsHref(hrefValue)
					};
				});

			return {
				...content,
				items: resolvedItems
			};
		},
		[
			buildDynamicPageSlug,
			buildVariableMap,
			draftLanguage,
			formatDynamicDate,
			latestArticleItemsByType,
			parseDynamicDate,
			resolveContentTypeConfig,
			resolveDynamicPageTemplate,
			resolveNewsHref,
			stringifyVariableValue
		]
	);

	const resolveVariableString = useCallback(
		(rawValue: any): string => {
			if (typeof rawValue === "string") return rawValue;
			if (rawValue && typeof rawValue === "object") {
				const src = (rawValue as { src?: string }).src;
				if (typeof src === "string") return src;
			}
			return stringifyVariableValue(rawValue);
		},
		[stringifyVariableValue]
	);

	const replaceVariablesInValue = useCallback(
		(value: any, variables: Record<string, any>, imageKeys?: Set<string>): any => {
			if (typeof value === "string") {
				const exactMatch = value.match(/^\{([^{}]+)\}$/);
				if (exactMatch) {
					const key = exactMatch[1]?.trim();
					if (key && imageKeys?.has(key)) {
						const replacement = variables[key];
						if (replacement && typeof replacement === "object") {
							return replacement;
						}
					}
				}
				return value.replace(/\{([^{}]+)\}/g, (match, key) => {
					const normalized = String(key ?? "").trim();
					if (!(normalized in variables)) return match;
					return resolveVariableString(variables[normalized]);
				});
			}
			if (Array.isArray(value)) {
				return value.map((entry) => replaceVariablesInValue(entry, variables, imageKeys));
			}
			if (value && typeof value === "object") {
				const result: Record<string, any> = {};
				Object.entries(value).forEach(([key, entry]) => {
					result[key] = replaceVariablesInValue(entry, variables, imageKeys);
				});
				return result;
			}
			return value;
		},
		[resolveVariableString]
	);

	const buildDynamicPreviewComponents = useCallback(
		(baseComponents: All8PageContent, item: ContentDocument | null, config?: ContentTypeConfig | null): All8PageContent => {
			if (!item) return baseComponents;
			const data = (item.data ?? {}) as Record<string, any>;
			const imageKeys = new Set<string>();
			(config?.fields ?? []).forEach((field) => {
				const key = (field.key ?? field.title ?? "").trim();
				if (!key) return;
				if ((field.type ?? "").trim().toLowerCase() === "image") {
					imageKeys.add(key);
				}
			});
			const variables = buildVariableMap(item, config);
			const output: All8PageContent = [];
			baseComponents.forEach((component) => {
				if (component.componentType === ComponentTypes.DynamicComponents) {
					const variableKey = (component.content as { variableKey?: string } | undefined)?.variableKey ?? "components";
					const dynamicRaw = Array.isArray(data[variableKey]) ? (data[variableKey] as any[]) : [];
					const dynamicComponents = normalizeComponents(dynamicRaw);
					dynamicComponents.forEach((dyn) => {
						const replaced = {
							...dyn,
							content: replaceVariablesInValue(dyn.content, variables, imageKeys)
						} as All8PageComponent;
						output.push(applyArticleHeroBreadcrumbs(replaced, data));
					});
					return;
				}
				const replaced = {
					...component,
					content: replaceVariablesInValue(component.content, variables, imageKeys)
				} as All8PageComponent;
				output.push(applyArticleHeroBreadcrumbs(replaced, data));
			});
			return output;
		},
		[applyArticleHeroBreadcrumbs, buildVariableMap, replaceVariablesInValue]
	);

	const resolveMarketsBoardDynamicContent = useCallback(
		(component: All8PageComponent): MarketsBoardContent => {
			const content = (component.content ?? {}) as MarketsBoardContent;
			if (component.componentType !== ComponentTypes.MarketsBoard) return content;
			const inflation = content.inflation;
			if (!inflation || inflation.mode !== "dynamic") return content;
			const dynamic = inflation.dynamicComponent ?? {};
			const contentTypeKey = (dynamic.contentTypeKey ?? "").toString().trim();
			if (!contentTypeKey) return content;
			const items = marketsBoardInflationItemsByType[contentTypeKey] ?? [];
			if (items.length === 0) return content;
			const language = (draftLanguage ?? DEFAULT_LANGUAGE).toLowerCase();
			const filtered = items.filter(
				(item) => !item?.isDeleted && (!item?.language || item.language === language)
			);
			if (filtered.length === 0) return content;

			const projection = projectMarketsInflation({
				rows: filtered,
				sortField: dynamic.sortField,
				fieldMapping: dynamic.fieldMapping ?? null,
				sparklineField: dynamic.sparklineField,
				sparklineWindow: dynamic.sparklineWindow
			});
			if (!projection) return content;

			// Strip authoring metadata from the previewed shape so the SDK
			// renderer treats this as a normal manual block (matches what the
			// deploy projector does to the production payload).
			const { mode: _mode, dynamicComponent: _dyn, ...inflationRest } = inflation;
			return {
				...content,
				inflation: {
					...inflationRest,
					asOf: projection.asOf ?? inflationRest.asOf,
					source: projection.source ?? inflationRest.source,
					rows: projection.rows.length > 0 ? projection.rows.map((r) => ({ ...r })) : inflationRest.rows,
					// Replace the previously-hardcoded sparkline only when the
					// projector produced one; otherwise pass through whatever
					// the editor authored manually.
					sparkline: projection.sparkline != null ? [...projection.sparkline] : inflationRest.sparkline
				}
			};
		},
		[draftLanguage, marketsBoardInflationItemsByType]
	);

	return {
		resolveNewsHref,
		normalizeSlugValue,
		replaceVariablesInSlug,
		buildDynamicPageSlug,
		normalizeContentTypeKey,
		resolveDynamicPageTemplate,
		resolveContentTypeConfig,
		buildVariableMap,
		resolveNewsSectionDynamicContent,
		resolvePaginatedActivityGridDynamicContent,
		resolveLatestArticleGridDynamicContent,
		resolveMarketsBoardDynamicContent,
		resolveVariableString,
		replaceVariablesInValue,
		buildDynamicPreviewComponents
	};
}
